import { Box, Typography, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import VideocamIcon from '@mui/icons-material/Videocam';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

const DashboardHero = ({ 
  greeting, 
  description, 
  showVideoCallButton = true,
  showScheduleButton = true,
  scheduleButtonDisabled = false,
  scheduleButtonTooltip = '',
  userType = 'physician', // for navigation context
  onStartVideoCall
}) => {
  const navigate = useNavigate();

  const handleVideoCall = () => {
    if (onStartVideoCall) {
      onStartVideoCall();
    } else {
      navigate(`/video-call?userType=${userType}`);
    }
  };

  const handleViewSchedule = () => {
    navigate(`/appointments?userType=${userType}`);
  };

  return (
    <Box
      sx={{
        background: 'linear-gradient(135deg, #25CEC8 0%, #1BA098 100%)',
        color: 'white',
        padding: { xs: '24px 20px', sm: '32px 32px', md: '40px 48px' },
        borderRadius: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: { xs: 2, sm: 3 },
      }}
    >
      {/* Left Side: Text and Buttons */}
      <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 auto' }, minWidth: { xs: '100%', sm: '300px' } }}>
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 700, 
            mb: { xs: 1.5, sm: 2 },
            fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
            lineHeight: 1.2,
          }}
        >
          {greeting}
        </Typography>
        
        <Typography 
          variant="body1" 
          sx={{ 
            mb: { xs: 2, sm: 2.5 }, 
            opacity: 0.95,
            lineHeight: 1.5,
            fontSize: { xs: '0.875rem', sm: '1rem' },
            maxWidth: '600px'
          }}
        >
          {description}
        </Typography>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: { xs: 1.5, sm: 2 }, flexWrap: 'wrap' }}>
          {showVideoCallButton && (
            <Button
              variant="contained"
              size="large"
              startIcon={<VideocamIcon />}
              onClick={handleVideoCall}
              sx={{
                backgroundColor: '#FF5A5A',
                color: 'white',
                px: 3,
                py: 1.5,
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '1rem',
                '&:hover': {
                  backgroundColor: '#E54545',
                },
                boxShadow: '0 4px 12px rgba(255, 90, 90, 0.3)',
              }}
            >
              Start Video Call
            </Button>
          )}

          {showScheduleButton && (
            <Button
              variant="outlined"
              size="large"
              startIcon={<CalendarMonthIcon />}
              onClick={handleViewSchedule}
              disabled={scheduleButtonDisabled}
              title={scheduleButtonTooltip}
              sx={{
                borderColor: scheduleButtonDisabled ? 'rgba(255, 255, 255, 0.3)' : 'white',
                color: scheduleButtonDisabled ? 'rgba(255, 255, 255, 0.5)' : 'white',
                px: 3,
                py: 1.5,
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '1rem',
                borderWidth: '2px',
                '&:hover': {
                  borderWidth: '2px',
                  borderColor: scheduleButtonDisabled ? 'rgba(255, 255, 255, 0.3)' : 'white',
                  backgroundColor: scheduleButtonDisabled ? 'transparent' : 'rgba(255, 255, 255, 0.1)',
                },
                '&.Mui-disabled': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  color: 'rgba(255, 255, 255, 0.5)',
                },
              }}
            >
              View Schedule
            </Button>
          )}
        </Box>
      </Box>

      {/* Right Side: Logo */}
      <Box
        sx={{
          flex: { xs: '0 0 100%', sm: '0 0 auto' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: { xs: 'center', sm: 'flex-end' },
          mt: { xs: 2, sm: 0 },
        }}
      >
        <Box
          component="img"
          src="/images/logo.png"
          alt="Alpha01 Medical Consultants"
          sx={{
            width: { xs: '120px', sm: '140px', md: '160px' },
            height: 'auto',
            backgroundColor: 'white',
            padding: { xs: 1.5, sm: 2 },
            borderRadius: 2,
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
          }}
        />
      </Box>
    </Box>
  );
};

export default DashboardHero;
